import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  withStyles,
} from '@material-ui/core';
import {
  canRetryCompletionRuleMigration,
  formatMigrationFidelitySummary,
} from '../../utils/migration-report.utils';
import styles from './migration-report-panel.style';

const fidelityChipColor = (fidelity) => {
  if (fidelity === '1to1') return 'primary';
  if (fidelity === 'partial' || fidelity === 'redesign') return 'default';
  return 'secondary';
};

const MigrationReportPlaceholder = ({ loading }) => (
  <Box data-testid={loading ? 'migration-report-panel-loading' : 'migration-report-panel-empty'}>
    <Typography variant="body2" color="textSecondary">
      {loading
        ? 'Loading completion-rule migration report…'
        : 'No completion-rule migration data for this course yet.'}
    </Typography>
  </Box>
);

const MigrationReportPanel = ({
  classes,
  migration,
  onRetry,
  retrying,
  loading,
  onResolveReviewItem,
  onDismissReviewItem,
  reviewActionRuleId,
}) => {
  if (!migration?.status) {
    return <MigrationReportPlaceholder loading={Boolean(loading)} />;
  }

  const { status, report } = migration;
  const summary = formatMigrationFidelitySummary(report);
  const mappedRules = Array.isArray(report?.mappedRules) ? report.mappedRules : [];
  const reviewQueue = Array.isArray(report?.reviewQueue) ? report.reviewQueue : [];
  const writeSummary = report?.writeSummary;

  return (
    <Box className={classes.root} data-testid="migration-report-panel">
      <Typography variant="subtitle2" className={classes.sectionLabel}>
        Completion rules (D2L → Canvas)
      </Typography>

      <Box className={classes.summaryRow}>
        <Chip size="small" label={`Status: ${status}`} data-testid="migration-report-status" />
        {canRetryCompletionRuleMigration(status) && onRetry ? (
          <Button
            size="small"
            color="primary"
            variant="outlined"
            disabled={retrying}
            onClick={onRetry}
            data-testid="migration-report-retry"
          >
            {retrying ? 'Retrying…' : 'Retry migration'}
          </Button>
        ) : null}
      </Box>

      {summary ? (
        <Typography variant="body2" color="textSecondary" data-testid="migration-report-fidelity">
          {summary}
        </Typography>
      ) : null}

      {(report?.orgUnitId || report?.canvasCourseId) && (
        <Typography variant="caption" color="textSecondary" display="block">
          D2L org unit: {report?.orgUnitId || '—'} · Canvas course: {report?.canvasCourseId || '—'}
        </Typography>
      )}

      {writeSummary ? (
        <Typography variant="caption" color="textSecondary" display="block">
          Writes — applied: {writeSummary.applied ?? 0}, unchanged: {writeSummary.unchanged ?? 0},
          skipped: {writeSummary.skipped ?? 0}, failed: {writeSummary.failed ?? 0}
        </Typography>
      ) : null}

      {report?.deferReason ? (
        <Typography variant="body2" color="error" data-testid="migration-report-defer-reason">
          Deferred: {report.deferReason}
        </Typography>
      ) : null}

      {report?.errorMessage ? (
        <Typography variant="body2" color="error" data-testid="migration-report-error">
          Error: {report.errorMessage}
        </Typography>
      ) : null}

      {mappedRules.length > 0 ? (
        <Box marginTop={2}>
          <Typography variant="subtitle2" className={classes.sectionLabel}>
            Mapped rules
          </Typography>
          <Table size="small" data-testid="migration-mapped-rules-table">
            <TableHead>
              <TableRow>
                <TableCell>D2L</TableCell>
                <TableCell>Canvas</TableCell>
                <TableCell>Fidelity</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mappedRules.map((rule) => (
                <TableRow key={rule.ruleId}>
                  <TableCell>
                    <Typography variant="body2">{rule.title}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {rule.d2l?.type || '—'}
                      {rule.d2l?.conditionTexts?.length
                        ? ` · ${rule.d2l.conditionTexts.join('; ')}`
                        : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {rule.canvas?.itemTitle || rule.canvas?.target || '—'}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      {rule.canvas?.requirementType || rule.canvas?.target || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={rule.fidelity || '—'}
                      color={fidelityChipColor(rule.fidelity)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      {reviewQueue.length > 0 ? (
        <Box marginTop={2}>
          <Typography variant="subtitle2" className={classes.sectionLabel}>
            Needs review ({reviewQueue.length})
          </Typography>
          <Table size="small" data-testid="migration-review-queue-table">
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Fidelity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviewQueue.map((item) => {
                const resolution = item.resolutionStatus || 'open';
                const busy = reviewActionRuleId === item.ruleId;
                return (
                  <TableRow key={item.ruleId}>
                    <TableCell>
                      <Typography variant="body2">{item.title}</Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {item.targetType}
                        {item.conditionTexts?.length ? ` · ${item.conditionTexts.join('; ')}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{item.reason}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={item.fidelity || '—'} color="secondary" />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={resolution} />
                    </TableCell>
                    <TableCell>
                      {resolution === 'open' && (onResolveReviewItem || onDismissReviewItem) ? (
                        <Box display="flex" style={{ gap: 8 }}>
                          {onResolveReviewItem ? (
                            <Button
                              size="small"
                              color="primary"
                              disabled={busy}
                              onClick={() => onResolveReviewItem(item.ruleId)}
                              data-testid={`migration-review-resolve-${item.ruleId}`}
                            >
                              Resolve
                            </Button>
                          ) : null}
                          {onDismissReviewItem ? (
                            <Button
                              size="small"
                              disabled={busy}
                              onClick={() => onDismissReviewItem(item.ruleId)}
                              data-testid={`migration-review-dismiss-${item.ruleId}`}
                            >
                              Dismiss
                            </Button>
                          ) : null}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {item.resolvedBy ? `by ${item.resolvedBy}` : '—'}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Box>
  );
};

MigrationReportPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  migration: PropTypes.shape({
    status: PropTypes.string,
    report: PropTypes.object,
  }),
  onRetry: PropTypes.func,
  retrying: PropTypes.bool,
  loading: PropTypes.bool,
  onResolveReviewItem: PropTypes.func,
  onDismissReviewItem: PropTypes.func,
  reviewActionRuleId: PropTypes.string,
};

MigrationReportPanel.defaultProps = {
  migration: null,
  onRetry: undefined,
  retrying: false,
  loading: false,
  onResolveReviewItem: undefined,
  onDismissReviewItem: undefined,
  reviewActionRuleId: null,
};

export default withStyles(styles)(MigrationReportPanel);
